import {
    AnimationMixer,
    Clock
} from 'da-three';
import { randomString } from '../../util/Helper.js';
import EventDispatcher from './../service/EventDispatcher.js';

export default class AnimationManager{
    constructor(mediator){
        this.mediator = mediator;
        this.mixers = {};
        this.animations = {};
        this.activeActions = [];
        this.hooks = [];
        this.hookQueue = {};
        this.animationBrakpoints = {}; 
        this.animationPointQueue = [];
        this.inAnimationHook = false;
        this.lastHookCheck = 0;
        this.lastAnimationTime = -1;
        this.animationPlaying = false;
        this.clock = new Clock();
        this.currentAnimationTime = 0;
        this.dispatcher = new EventDispatcher();
        this.dispatcher.setEventTypes([
            "animationRunning",
            "animationHook",
            "animationState"
        ]);
        
        this.mediator.addEventListener('beforeRender', this.onBeforeRender.bind(this));
        this.mediator.addEventListener('objectComplete', this.onObjectComplete.bind(this));

        this.dispatcher.addEventListener('animationHook', this.onAnimationHook.bind(this));
    }
    
    addTimelineHook(key, time, unit, payload) {
        this.hooks.push({
            key,
            time, 
            unit,
            payload
        });

        if (this.activeActions[0]) {
            this.setupHooks();
        }
    }

    onAnimationHook(event, data) {
        const hook = this.animationBrakpoints[data.key];
        if (hook) {
            if (hook.callback && typeof hook.callback === 'function') {
                hook.callback(data);
            }
            if (this.animationPointQueue.length > 0) {
                this.dispatchNextAnimationPoint(true);
            } else {
                this.inAnimationHook = false;
                this.animationToggle(false);
                this.setCurrentAnimationTimeScale(1);
            }
            
            this.removeTimelineHook(data.key);
            delete this.animationBrakpoints[data.key];
        }
    }

    removeTimelineHook(key) {
        this.hooks = this.hooks.filter(h => h.key !== key);

        if (this.activeActions[0]) {
            this.setupHooks();
        }
    }

    animateTo(options, onComplete) {
        const key = randomString(10);
        
        this.addTimelineHook(key, options.time, options.unit, onComplete);
        this.animationBrakpoints[key] = {
            ...options,
            callback: onComplete
        };

        const realTime = this.getRealTime(options);
        options.realTime = realTime;
        this.animationPointQueue.push(options);
        if (!this.animationPlaying) {
            this.inAnimationHook = true;
            this.dispatchNextAnimationPoint(true);
        } else if (this.animationPlaying && !this.inAnimationHook) {
            this.dispatchNextAnimationPoint(true);
        }
    }

    setCurrentAnimationTimeScale(scale) {
        if (isNaN(scale)) {
            return;
        }
        this.activeActions.forEach(action => {
            action.timeScale = scale;
        });
    }

    dispatchNextAnimationPoint(force = false) {
        if ((!this.animationPlaying || force === true) && this.animationPointQueue.length > 0) {
            const next = this.animationPointQueue.splice(0,1)[0]; // get first inserted Element LIFO
            
            const action = this.activeActions[0];
            //const duration = action.getClip().duration;
            const time = action.time;  

            if (next.teleport) {
                this.lastHookCheck = action.time;
                action.time = next.realTime;
                this.checkHooks(true);
                if (this.animationPlaying) {
                    this.animationToggle(false);
                }
                this.setCurrentAnimationTimeScale(1);
                return;
            }

            if (time > next.realTime) {
                this.setCurrentAnimationTimeScale(-1);
            } else {
                this.setCurrentAnimationTimeScale(1);
            }

            this.animationToggle(true);
        }
    }
    
    animationToggle(play) {
        this.animationPlaying = play;
        let aLength = this.activeActions.length;
        
        for (let i=0;i<aLength;i++){
            if (play){
                this.activeActions[i].play();
                this.activeActions[i].paused = false;
            } else {
                this.activeActions[i].paused = true;
            }
        }
    }
    
    
    
    getAnimation(objectID,animName){
        if (typeof(this.animations[objectID]) !== 'undefined'){
            let aLength = this.animations[objectID].length;
            for (let i=0;i<aLength;i++){
                if (this.animations[objectID][i].name === animName){
                    return this.animations[objectID][i];
                }
            }
        } 
        
        return false;
    }
    
    
    
    getAnimationNamesByObjectID(id) {
        if (typeof(this.animations[id]) !== 'undefined' && this.animations[id].length > 0) {
            let animations = [];
            let aLength = this.animations[id].length
            for (let i=0;i<aLength;i++){
                animations.push(this.animations[id][i].name);
            }
            return animations;
        } 
            
        return false;
    }
    
    
    
    getMixerByObjectID(id) {
        if (typeof(this.mixers[id]) !== 'undefined'){
            return this.mixers[id];
        } 
        
        return false;
    }
    
    
    
    onBeforeRender() {
        const delta = this.clock.getDelta();
        for ( var key in this.mixers ) {
            if (this.mixers.hasOwnProperty(key)) {
                this.mixers[key].update(delta);
                //console.log(this.mixers[key])
                //this.currentAnimationTime = this.mixes[key]
            }
        }

        let aLength = this.activeActions.length;
        for (let i = 0; i < aLength; i++){
            let action = this.activeActions[i];

            this.currentAnimationTime = action.time;

            if (this.activeActions[i].isRunning){
                this.dispatcher.dispatch("animationRunning",this.activeActions[i]);
            }
        }

        if (this.animationPlaying) {
            this.checkHooks();
        }

        this.notifyCurrentAnimationState();
    }

    notifyCurrentAnimationState(force = false) {
        if (this.activeActions.length > 0) {
            const action = this.activeActions[0];
            const duration = action.getClip().duration;
            const time = action.time;
            if (time != this.lastAnimationTime || force) {
                this.dispatcher.dispatch("animationState", {
                    time,
                    duration,
                    playing: this.animationPlaying,
                    forcedUpdate: force
                });

                this.lastAnimationTime = time;
            }
            
        }
    }
    
    checkHooks(teleported = false) {
        const action = this.activeActions[0];
        if (!action) {
            return;
        }
        
        const duration = action.getClip().duration;
        const time = action.time;        
        const timeSinceCheck = time - this.lastHookCheck;

        if (timeSinceCheck === 0 || (-(timeSinceCheck) / duration) > 0.9) {
            // return if no progress or if jump was very whide into past
            return;
        }
        
        // get start and end second
        let start = Math.floor((time - timeSinceCheck) * 10); // scale to tenth second
        let end = Math.floor(time * 10);
        if (start > end) {
            // swap to always get positive loop
            const tmp = start;
            start = end;
            end = tmp;
        }
        const milliTime = (time * 1000) | 0; // check as milliseconds
        const treshold = Math.abs((timeSinceCheck * 1000) | 0);
        for (let i = start; i <= end; i++) {
            if (this.hookQueue[i]) {
                const subQueue = this.hookQueue[i];
                subQueue.filter(h => {
                        const time = Math.abs(((h.real * 1000) | 0) - milliTime)
                        return time < treshold || (h.lastFired === -1 && h.real * 1000 < milliTime);
                    }).forEach(h => {
                        h.lastFired = milliTime;
                        this.dispatcher.dispatch('animationHook', {
                            key: h.key,
                            animating: this.animationPlaying,
                            teleported,
                            payload: h.payload,
                            time: time,
                            relative: time / duration
                        });
                    });
            }
        }

        if ((this.lastHookCheck / duration) > 0.9 && (time /duration) < 0.1) {
            // loop detected, reset all firetimes
            Object.keys(this.hookQueue).forEach(k => {
                this.hookQueue[k].forEach(h => { 
                    h.lastFired = -1;
                });
            });
        }

        this.lastHookCheck = time;
        
        //console.log("Checking Animations", this.animationPlaying, time, "Delta" , timeSinceCheck, "from", start, "to", end);
    }

    getRealTime(elem, duration) {
        switch (elem.unit) {
            case 'r':
                return elem.time * duration;
            case 's':
                return elem.time;
            case 'm':
                return (elem.time * 60);
            default:
                console.error(`Unknown time unit ${elem.unit}, using relative scaling`);
                return elem.time;
        }
    }

    setupHooks() {
        const action = this.activeActions[0];
        const clip = action.getClip();
        const duration = clip.duration;
        
        if (action) {
            this.hookQueue = this.hooks
                .map(el => {
                    el.real = this.getRealTime(el, duration);
                    el.lastFired = -1;
                    return el;
                })
                .sort((a, b) => a.real > b.real)
                .reduce((obj, next) => {
                    const idx = Math.floor((next.real * 10));
                    if (!obj[idx]) {
                        obj[idx] = [];
                    }
                    obj[idx].push(next);
                    return obj;
                }, {});
            
            //console.log("Hookqueue", this.hookQueue);
            return true;
        }


        return false;
    }
    
    onObjectComplete(event,object) {
        if (typeof(object.animations) !== 'undefined' && object.animations.length > 0){
            this.mixers[object.id] = new AnimationMixer(object);
            this.animations[object.id] = object.animations;
            delete object.animations;
        }
    }
    
    
    
    selectAnimation(objectID,animName){
        let animation = this.getAnimation(objectID,animName);
        if (animation !== false && typeof(this.mixers[objectID]) !== 'undefined'){
            let animState = this.animationPlaying===true?true:false;
            this.animationToggle(false);
            this.activeActions = [];
            const action = this.mixers[objectID].clipAction(animation);
            this.activeActions.push(action);

            this.lastAnimationTime = -1;
            // animationhooks
            this.setupHooks();

            if (animState){
                this.animationToggle(true);
            }
            
            return true;
        }
        
        return false;
    }
    
    
    
    setAnimationTime(time, force = false) {
        // TODO check difference and find hooks to be fired?

        let aLength = this.activeActions.length;
        this.animationTime = time;
        for (let i=0;i<aLength;i++){
            let action = this.activeActions[i]
            let clip = action.getClip();
            if (time >= 0 && time <= clip.duration){
                action.time = time;
                
                //If animation has never been started, start and pause it
                if (!action.isRunning() && !action.paused){
                    action.play();
                    action.paused = true;
                }
            }
        }

        this.notifyCurrentAnimationState(force);
        this.checkHooks();
    }
}


// WEBPACK FOOTER //
// ./src/js/studio/manager/AnimationManager.js